import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Badge } from "@/components/Badge/Badge.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { CheckmarkMedium, Icon12 } from "@/icons/Icons.ts"
import { shortRevisionId } from "@/lib/displayUtils.ts"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { ProjectPooledPreviewViewModel } from "@/components/ProjectSettingsDialog/ProjectPooledPreviewViewModel.ts"
import type { UPreviewProcessesApiState } from "@/lib/client/generated/TypeDefinitions.ts"

export const ProjectPooledPreviewTab = observer((props: {
  model: ProjectPooledPreviewViewModel
}) => {
  const vm = props.model

  if (vm.isLoading.get()) {
    return (
      <Vertical gap="16px">
        <Heading2>Pooled Preview</Heading2>

        <Spinner text={ellipsize("Loading")} />
      </Vertical>
    )
  }

  const settings = vm.settings.get()
  const poolingEnabled = settings?.previewPoolingEnabled ?? false
  const pooledPreviewId = vm.pooledPreviewId.get()
  const previewState: UPreviewProcessesApiState | null = vm.pooledPreviewState.get()

  if (!poolingEnabled) {
    return (
      <Vertical gap="16px">
        <Heading2>Pooled Preview</Heading2>

        <Description>
          Pooled preview keeps a preview environment running for this project,
          providing faster access when starting new ideas.
        </Description>

        <InfoMessage>
          Pooled preview is currently disabled at the application level.
        </InfoMessage>
      </Vertical>
    )
  }

  const getStatusBadge = () => {
    if (!pooledPreviewId) {
      return (
        <Badge kind="info" variant="small" textTransform="uppercase">
          No preview in pool
        </Badge>
      )
    }

    if (!previewState) {
      return (
        <Badge kind="info" variant="small" textTransform="uppercase">
          {ellipsize("Loading status")}
        </Badge>
      )
    }

    if (previewState.className === "Starting") {
      return (
        <Badge kind="info" variant="small" textTransform="uppercase">
          Starting
        </Badge>
      )
    } else if (previewState.className === "Running") {
      const allProcessesRunning = previewState.processStates.every(
        (pair) => pair.second.className === "Running"
      )
      const previewPortReady = previewState.previewPortStatus?.second === true

      if (allProcessesRunning && previewPortReady) {
        return (
          <Badge kind="success" variant="small" textTransform="uppercase">
            <CheckmarkMedium className={Icon12} />
            Ready
          </Badge>
        )
      } else {
        return (
          <Badge kind="info" variant="small" textTransform="uppercase">
            Starting processes
          </Badge>
        )
      }
    } else if (previewState.className === "Failed") {
      return (
        <Badge kind="error" variant="small" textTransform="uppercase">
          Failed
        </Badge>
      )
    } else if (previewState.className === "Stopped") {
      return (
        <Badge kind="disabled" variant="small" textTransform="uppercase">
          Stopped
        </Badge>
      )
    }

    return null
  }

  return (
    <Vertical gap="16px">
      <Heading2>Pooled Preview</Heading2>

      <Description>
        Pooled preview keeps a preview environment running for this project,
        providing faster access when starting new ideas.
      </Description>

      <StatusSection gap="12px">
        <Horizontal gap="8px" alignItems="center">
          <StatusLabel>Preview status:</StatusLabel>
          {getStatusBadge()}
        </Horizontal>

        {pooledPreviewId && previewState?.className === "Running" && (
          <PreviewDetails gap="8px">
            <DetailItem>
              <DetailLabel>Commit:</DetailLabel>
              <DetailValue>{shortRevisionId(previewState.commitHash)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Processes:</DetailLabel>
              <DetailValue>
                {previewState.processStates.filter((pair) => pair.second.className === "Running").length} / {previewState.processStates.length} running
              </DetailValue>
            </DetailItem>
            {previewState.previewPortStatus && (
              <DetailItem>
                <DetailLabel>Preview port:</DetailLabel>
                <DetailValue>
                  {previewState.previewPortStatus.first} ({previewState.previewPortStatus.second ? "ready" : "not ready"})
                </DetailValue>
              </DetailItem>
            )}
          </PreviewDetails>
        )}
      </StatusSection>

      {vm.error.get() && (
        <ErrorMessage>{vm.error.get()}</ErrorMessage>
      )}
    </Vertical>
  )
})

const Description = styled.div`
  color: var(--secondary-text-color);
  ${smallFontSize};
`

const StatusSection = styled(Vertical)`
  padding: 12px;
  background: var(--text-color-3);
  border-radius: var(--border-radius-12);
`

const StatusLabel = styled.div`
  color: var(--text-color);
  ${smallFontSize};
  font-weight: 500;
`

const PreviewDetails = styled(Vertical)`
  padding-left: 8px;
`

const DetailItem = styled(Horizontal)`
  gap: 8px;
  align-items: center;
`

const DetailLabel = styled.div`
  color: var(--secondary-text-color);
  ${smallFontSize};
`

const DetailValue = styled.div`
  color: var(--text-color);
  ${smallFontSize};
`

const InfoMessage = styled.div`
  padding: 12px;
  background: var(--text-color-3);
  border-radius: var(--border-radius-12);
  color: var(--secondary-text-color);
  ${smallFontSize};
`

const ErrorMessage = styled.div`
  padding: 12px;
  background: var(--error-color-10);
  border-radius: var(--border-radius-12);
  color: var(--error-color);
  ${smallFontSize};
`
